import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  LocalShipping as CargoIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CargoSystemsContractDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Cargo Systems O&M and Development Contract
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            $184 Million - CBP Cargo Services Program Directorate
          </Typography>
        </Box>
      </Box>

      {/* Contract Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group serves as the prime contractor for a $184 million contract providing comprehensive 
            application development and operational support across CBP's Cargo Services Program Directorate. This 
            mission-critical contract supports the systems that process imports, exports, compliance, and trade 
            facilitation for the United States' international trade operations, handling billions of dollars in 
            commerce annually.
          </Typography>
        </CardContent>
      </Card>

      {/* Contract Details */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Details
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="Cargo Systems Contract Details">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '25%', bgcolor: '#003366', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Award ID
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>70B04C19F00000204</strong>
                  </Typography>
                  <Link href="https://www.usaspending.gov/award/CONT_AWD_70B04C19F00000204_7014_GS35F0897R_4730" target="_blank" rel="noopener">
                    <Typography variant="caption">View on USASpending.gov</Typography>
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Value
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>$184,000,000</strong> (One Hundred Eighty-Four Million Dollars)
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Award Date
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>2019</strong> - Multi-year contract
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contracting Office
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>CBP Cargo Services Program Directorate</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    U.S. Customs and Border Protection
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Type
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Application Development and O&M Support</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Operations & Maintenance, Custom Development, Modernization
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Status
                </TableCell>
                <TableCell>
                  <Chip label="Active" color="success" />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Ongoing operations and development support
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Scope of Work */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CargoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Scope of Work
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CargoIcon sx={{ fontSize: 40, color: '#003366', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold' }}>
                  Import/Export Processing Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Development and support for systems that process international trade.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Import Processing:</strong> Entry filing, duty calculation, release processing</Typography></li>
                <li><Typography variant="body2"><strong>Export Processing:</strong> Export declarations, manifest processing</Typography></li>
                <li><Typography variant="body2"><strong>Cargo Tracking:</strong> Real-time shipment monitoring and status</Typography></li>
                <li><Typography variant="body2"><strong>Document Management:</strong> Electronic filing and storage systems</Typography></li>
                <li><Typography variant="body2"><strong>Payment Processing:</strong> Duty and fee collection systems</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Compliance & Trade Facilitation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Enforcement and compliance systems for trade regulations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Compliance:</strong> Tariff classification, valuation, origin verification</Typography></li>
                <li><Typography variant="body2"><strong>Risk Assessment:</strong> Automated targeting and screening systems</Typography></li>
                <li><Typography variant="body2"><strong>Enforcement:</strong> Violation tracking, penalties, seizures</Typography></li>
                <li><Typography variant="body2"><strong>Trade Agreements:</strong> USMCA, FTA compliance verification</Typography></li>
                <li><Typography variant="body2"><strong>Reporting:</strong> Trade statistics, compliance metrics</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Custom Development Services
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Application development and enhancement services.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agile Development:</strong> Scrum teams, sprint-based delivery</Typography></li>
                <li><Typography variant="body2"><strong>Full-Stack Development:</strong> Frontend, backend, database development</Typography></li>
                <li><Typography variant="body2"><strong>API Development:</strong> RESTful services, microservices architecture</Typography></li>
                <li><Typography variant="body2"><strong>Integration:</strong> System-to-system interfaces, data exchange</Typography></li>
                <li><Typography variant="body2"><strong>Testing:</strong> Automated testing, QA, performance testing</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Operations & Maintenance Support
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                24/7 operational support and system maintenance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>24/7 Support:</strong> Round-the-clock system monitoring and support</Typography></li>
                <li><Typography variant="body2"><strong>Incident Management:</strong> Issue resolution, root cause analysis</Typography></li>
                <li><Typography variant="body2"><strong>System Maintenance:</strong> Patches, updates, performance tuning</Typography></li>
                <li><Typography variant="body2"><strong>User Support:</strong> Help desk, training, documentation</Typography></li>
                <li><Typography variant="body2"><strong>Disaster Recovery:</strong> Backup, recovery, business continuity</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Systems Supported */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Key Systems Supported
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Purpose</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Users</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Volume</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>ACE (Automated Commercial Environment)</strong></TableCell>
              <TableCell>Trade processing and cargo release</TableCell>
              <TableCell>Importers, brokers, carriers</TableCell>
              <TableCell>30M+ entries/year</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Cargo Systems</strong></TableCell>
              <TableCell>Import/export manifest processing</TableCell>
              <TableCell>Trade community, CBP officers</TableCell>
              <TableCell>100M+ shipments/year</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Trade Compliance Systems</strong></TableCell>
              <TableCell>Enforcement, penalties, audits</TableCell>
              <TableCell>CBP enforcement, trade specialists</TableCell>
              <TableCell>Millions of transactions</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Partner Government Agencies (PGA)</strong></TableCell>
              <TableCell>Multi-agency clearance coordination</TableCell>
              <TableCell>FDA, USDA, EPA, other agencies</TableCell>
              <TableCell>Cross-agency processing</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Trade Data Systems</strong></TableCell>
              <TableCell>Statistics, reporting, analytics</TableCell>
              <TableCell>CBP analysts, trade community</TableCell>
              <TableCell>Billions in trade value</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Technical Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Technical Capabilities Delivered
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#003366' }}>
                Development Technologies
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Java, .NET, Python development</Typography></li>
                <li><Typography variant="body2">React, Angular frontend frameworks</Typography></li>
                <li><Typography variant="body2">Oracle, SQL Server databases</Typography></li>
                <li><Typography variant="body2">RESTful APIs, microservices</Typography></li>
                <li><Typography variant="body2">Cloud platforms (AWS, Azure)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                DevSecOps & Automation
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">CI/CD pipelines (Jenkins, GitLab)</Typography></li>
                <li><Typography variant="body2">Automated testing frameworks</Typography></li>
                <li><Typography variant="body2">Container orchestration (Kubernetes)</Typography></li>
                <li><Typography variant="body2">Infrastructure as Code (Terraform)</Typography></li>
                <li><Typography variant="body2">Security scanning and compliance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                Operations & Support
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">24/7 NOC operations</Typography></li>
                <li><Typography variant="body2">Incident management (ServiceNow)</Typography></li>
                <li><Typography variant="body2">Performance monitoring (Splunk)</Typography></li>
                <li><Typography variant="body2">Help desk and user support</Typography></li>
                <li><Typography variant="body2">Disaster recovery and backup</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contract Value & Impact */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Value & Business Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 'bold' }}>
                $184M
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Multi-year agreement
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                30M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Entries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Import entries processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                $2.5T+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Trade Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Annual U.S. trade supported
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Continuous support
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Mission Impact & Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Streamlined import/export processing</Typography></li>
                <li><Typography variant="body2"><strong>Revenue Collection:</strong> Efficient duty and fee collection systems</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Enforcement:</strong> Automated trade law enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Border Security:</strong> Risk-based cargo screening and targeting</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>System Reliability:</strong> 99.9%+ uptime for mission-critical systems</Typography></li>
                <li><Typography variant="body2"><strong>Modernization:</strong> Legacy system upgrades and cloud migration</Typography></li>
                <li><Typography variant="body2"><strong>User Experience:</strong> Improved interfaces for trade community</Typography></li>
                <li><Typography variant="body2"><strong>Cost Efficiency:</strong> Optimized operations and reduced processing times</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reference Links */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract References & Documentation
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Link href="https://www.usaspending.gov/award/CONT_AWD_70B04C19F00000204_7014_GS35F0897R_4730" target="_blank" rel="noopener">
                <Typography variant="body2">USASpending.gov - Contract Award Details</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://www.usaspending.gov/recipient/d768b60a-beaa-2e46-7aa5-6df63e232bb2-C/latest" target="_blank" rel="noopener">
                <Typography variant="body2">Dev Technology Group - Recipient Profile</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://www.federalcompass.com/federal-government-awarded-contracts/by-gov/US-Customs-and-Border-Protection-(CBP)" target="_blank" rel="noopener">
                <Typography variant="body2">Federal Compass - CBP Contracts Database</Typography>
              </Link>
            </li>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CargoSystemsContractDetail;
